import type React from "react";
import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Grid,
	Snackbar,
	Typography,
	CircularProgress,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { ErrorModal } from "@dexteel/mesf-core";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import {
	linkTemplateDrawings,
	unLinkTemplateDrawings,
} from "@/pages/templates/repositories/TemplatesRepositorie";
import type { Drawing } from "../../../../models/TemplateDrawing";
import { DrawingTable } from "./drawings-tables";

interface Props {
	searchDrawings: Function;
	show: boolean;
	isLoading: boolean;
	onHide: (shouldUpdate: boolean) => void;
	TemplateId: number | null;
	setShowLinkResourcesToAssetDialog: Function;
}

export const DrawingPicker: React.FC<Props> = ({
	searchDrawings,
	show,
	onHide,
	TemplateId,
	isLoading,
	setShowLinkResourcesToAssetDialog,
}) => {
	const {
		state: { listAllDrawings, templateDrawingsList },
		actions: { setTemplateDrawingToAdd },
	} = useTemplatesContext();

	const [selectedDrawings, setSelectedDrawings] = useState<Drawing[]>([]);
	const [error, setError] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [loadingSave, setLoadingSave] = useState(false);

	useEffect(() => {
		if (show) {
			searchDrawings();
		}
	}, [show]);

	useEffect(() => {
		if (show) {
			setSelectedDrawings(templateDrawingsList);
		}
	}, [show, templateDrawingsList]);

	const handleSelectionChanged = (newSelectedDrawings: Drawing[]) => {
		setSelectedDrawings(newSelectedDrawings);
	};

	const handleSave = async () => {
		setLoadingSave(true);
		const drawingsToAdd = selectedDrawings.filter(
			(drawing) =>
				!templateDrawingsList.some(
					(wdDrawing) => wdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		const drawingsToRemove = templateDrawingsList.filter(
			(drawing) =>
				!selectedDrawings.some(
					(sdDrawing) => sdDrawing.DocumentName === drawing.DocumentName,
				),
		);

		if (drawingsToAdd.length > 0) {
			const newDrawings = drawingsToAdd.map((drawing) => ({
				DocumentName: drawing.DocumentName ?? "",
				IsAssetResource: false,
			}));
			setTemplateDrawingToAdd(drawingsToAdd);
			await linkTemplateDrawings(TemplateId as number, newDrawings);
			setLoadingSave(false);

			setShowLinkResourcesToAssetDialog(true);

			setSuccessMessage("Drawings added successfully");
		}

		if (drawingsToRemove.length > 0) {
			const drawingsToRemoveFormatted = drawingsToRemove.map((drawing) => ({
				DocumentName: drawing.DocumentName ?? "",
				IsAssetResource: drawing.IsAssetResource,
			}));
			await unLinkTemplateDrawings(
				TemplateId as number,
				drawingsToRemoveFormatted,
			);
			setSuccessMessage("Drawings removed successfully");
		}

		if (drawingsToAdd.length > 0 || drawingsToRemove.length > 0) {
			onHide(true);
			setLoadingSave(false);
		} else {
			onHide(false);
			setLoadingSave(false);
		}
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide(false)} maxWidth="md" fullWidth>
				<DialogTitle style={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" style={{ margin: "0", fontWeight: 600 }}>
						DRAWINGS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<DrawingTable
						allDrawings={listAllDrawings}
						templateDrawings={templateDrawingsList}
						onSelectionChanged={handleSelectionChanged}
						isLoading={isLoading}
					/>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px 24px" }}>
					<Grid container spacing={2} justifyContent="flex-end">
						<Grid item>
							<Button
								variant="contained"
								color="default"
								onClick={() => onHide(false)}
							>
								Cancel
							</Button>
						</Grid>
						<Grid item>
							<Button
								startIcon={loadingSave && <CircularProgress size="1rem" />}
								disabled={loadingSave}
								variant="contained"
								color="primary"
								onClick={handleSave}
							>
								Save
							</Button>
						</Grid>
					</Grid>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={!!successMessage}
				autoHideDuration={6000}
				onClose={() => setSuccessMessage("")}
			>
				<Alert onClose={() => setSuccessMessage("")} severity="success">
					{successMessage}
				</Alert>
			</Snackbar>
		</>
	);
};
